import React, { useContext, useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Prefixes, Suffixes } from '../../enums/ContactEnums';
import {
  ContactsDocument,
  ICompany,
  IContactsQuery,
  IInterest,
  ITitleInternal,
  useCompaniesNamesQuery,
  useCreateContactMutation,
  useGetInterestsQuery,
  useTitlesInternalQuery
} from '../../generated/graphql';
import { fullNameExists, sameContactEmailExists } from '../../utils/clientUtils';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { getLabelColor, getRequiredFieldLabelColor } from '../../utils/styleUtils';
import DialogBox from '../Common/Layout/DialogBox';
import { Box, Divider } from '@mui/material';
import AutocompleteComponent from '../Common/Fields/AutocompleteComponent';
import InputComponent from '../Common/Fields/InputComponent';
import { Call, Email, LocationOn, Print, StayCurrentPortrait } from '@mui/icons-material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Colors } from '../../utils/theme';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import CheckboxComponent from '../Common/Fields/CheckboxComponent';
import { validEmailRegex } from '../../utils/ui';

type IProps = {
  handleClose: () => void;
};

export type IErrors = {
  firstName: string | null;
  lastName: string | null;
  titleInternal: string | null;
  company: string | null;
  email: string | null;
  updateError?: ApolloError;
  interests: string | null;
};

export const ErrorTexts = {
  company: 'Company is required',
  titleInternal: 'Title internal is required',
  email: 'Invalid email',
  emailExists: 'Email is taken',
  firstName: 'First name too short',
  lastName: 'Last name too short',
  interests: '*Please select at least one interest'
};

const defaultErrorsObj: IErrors = {
  firstName: null,
  lastName: null,
  titleInternal: null,
  company: null,
  email: null,
  interests: null
};

const NewContact: React.FunctionComponent<IProps> = ({ handleClose }: IProps) => {
  // const classes = useStyles();
  const titlesInternal = useTitlesInternalQuery();
  const companies = useCompaniesNamesQuery();
  const interests = useGetInterestsQuery({ fetchPolicy: 'no-cache' });
  const [allInterests, setAllInterests] = useState<IInterest[]>([]);
  const [prefix, setPrefix] = useState<string | null>(null);
  const [suffix, setSuffix] = useState<string | null>(null);
  const [titleInternal, setTitleInternal] = useState<ITitleInternal | null>(null);
  const [company, setCompany] = useState<ICompany | null>(null);
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [informalName, setInformalName] = useState('');
  const [titleExternal, setTitleExternal] = useState('');
  const [notes, setNotes] = useState('');
  const [phoneDirect, setPhoneDirect] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [faxDirect, setFaxDirect] = useState('');
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [linkedIn, setLinkedIn] = useState('');
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [createContact, createContactResult] = useCreateContactMutation();
  const { dispatch } = useContext(AppContext);
  const [contactInterestIds, setContactInterestIds] = useState<string[]>([]);

  useEffect(() => {
    if (interests.data?.result) {
      const dbInterests = interests.data.result as IInterest[];
      setAllInterests(dbInterests);
      setContactInterestIds(dbInterests.map((i) => i.id));
    }
  }, [interests.data]);

  const handleCheckClicked = (interest_id: string) => {
    if (contactInterestIds.includes(interest_id)) {
      setContactInterestIds(contactInterestIds.filter((id) => id !== interest_id));
      if (contactInterestIds.length === 1 || contactInterestIds.length === 0) {
        setFormErrors({ ...formErrors, interests: ErrorTexts.interests });
      }
    } else {
      setContactInterestIds([interest_id, ...contactInterestIds]);
      setFormErrors({ ...formErrors, interests: null });
    }
  };

  const handlePrefixChange = (_: unknown, newValue: React.SetStateAction<string | null>) => {
    setPrefix(newValue);
  };

  const handleSuffixChange = (_: unknown, newValue: React.SetStateAction<string | null>) => {
    setSuffix(newValue);
  };

  const handleTitleInternalChange = (
    _: unknown,
    newValue: React.SetStateAction<ITitleInternal | null>
  ) => {
    if (newValue) {
      setFormErrors({ ...formErrors, titleInternal: null });
    } else {
      setFormErrors({ ...formErrors, titleInternal: ErrorTexts.titleInternal });
    }
    setTitleInternal(newValue);
  };

  const handleCompanyChange = (_: unknown, newValue: React.SetStateAction<ICompany | null>) => {
    if (newValue) {
      setFormErrors({ ...formErrors, company: null });
    } else {
      setFormErrors({ ...formErrors, company: ErrorTexts.company });
    }
    setCompany(newValue);
  };

  const handleFirstNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        firstName: ErrorTexts.firstName
      });
    } else {
      setFormErrors({ ...formErrors, firstName: null });
    }
    setFirstName(value);
  };

  const handleLastNameChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        lastName: ErrorTexts.lastName
      });
    } else {
      setFormErrors({ ...formErrors, lastName: null });
    }
    setLastName(value);
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    const isValidEmail = value.match(validEmailRegex);
    if (!isValidEmail) {
      setFormErrors({
        ...formErrors,
        email: ErrorTexts.email
      });
    } else {
      setFormErrors({ ...formErrors, email: null });
    }
    setEmail(value);
  };

  const submitForm = () => {
    const errors = {
      company: null,
      email: null,
      firstName: null,
      lastName: null,
      titleInternal: null,
      interests: null
    } as IErrors;

    let hasErrors = false;
    if (!company) {
      hasErrors = true;
      errors.company = ErrorTexts.company;
    }
    if (!titleInternal) {
      hasErrors = true;
      errors.titleInternal = ErrorTexts.titleInternal;
    }
    if (!firstName || firstName.length < 2) {
      hasErrors = true;
      errors.firstName = ErrorTexts.firstName;
    }
    if (contactInterestIds.length === 0) {
      hasErrors = true;
      errors.interests = ErrorTexts.interests;
    }
    if (!lastName || lastName.length < 2) {
      hasErrors = true;
      errors.lastName = ErrorTexts.lastName;
    }
    const isValidEmail = email.match(validEmailRegex);
    if (!isValidEmail) {
      hasErrors = true;
      errors.email = ErrorTexts.email;
    }
    // Email uniqueness is checked on the backend - frontend cache check is unreliable with filtered/paginated queries
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      const nameExists = fullNameExists(firstName, lastName, company?.name as string);
      if (nameExists) {
        dispatch({
          type: Actions.ShowConfirmation,
          payload: {
            text: 'There is a contact with the same first and last name. Are you sure you want to continue?',
            agreeAction: submitContact
          }
        });
      } else {
        submitContact();
      }
    }
  };

  const submitContact = () => {
    dispatch({ type: Actions.HideConfirmation });
    createContact({
      variables: {
        input: {
          prefix,
          first_name: firstName,
          middle_name: middleName,
          last_name: lastName,
          suffix,
          informal_name: informalName,
          title_internal_id: parseInt(titleInternal?.id as string),
          title_external: titleExternal,
          company_id: parseInt(company?.id as string),
          notes,
          phone_direct: phoneDirect,
          phone_mobile: mobilePhone,
          fax: faxDirect,
          email,
          location_of_contact: location,
          interest_ids: contactInterestIds.map((i) => parseInt(i)),
          linkedin: linkedIn
        }
      },
      update(cache, { data }) {
        if (data?.result) {
          const contactsQuery = cache.readQuery({
            query: ContactsDocument
          }) as IContactsQuery;

          // Check if contactsQuery and contactsQuery.result exist before proceeding
          if (contactsQuery && contactsQuery.result) {
            const cacheContact = contactsQuery.result.find((c) => c?.id === data.result.id);
            if (!cacheContact) {
              const newData = [data.result, ...contactsQuery.result];
              cache.writeQuery({
                query: ContactsDocument,
                data: { result: newData }
              });
            }
          } else {
            // If no existing contacts in cache, create a new array with just this contact
            cache.writeQuery({
              query: ContactsDocument,
              data: { result: [data.result] }
            });
          }

          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `Contact ${data.result.fullName} created`,
              severity: 'success',
              autoHide: 2000
            }
          });
          handleClose();
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message,
          autoHide: 6000
        }
      });
    });
  };

  return (
    <DialogBox
      id="new-contact"
      width={1400}
      title="Create Contact"
      onClose={handleClose}
      childrenSx={{ padding: '40px 80px' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          gap: '60px'
        }}
      >
        <Box sx={{ gridColumn: 'span 3', display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <AutocompleteComponent
            id="contact-prefix"
            label="Prefix"
            options={Prefixes}
            getOptionLabel={(option: string) => option}
            disabled={createContactResult.loading}
            values={prefix}
            onChange={handlePrefixChange}
          />
          <InputComponent
            id="first-name"
            label="First Name"
            value={firstName}
            onChange={handleFirstNameChange}
            disabled={createContactResult.loading}
            errorMsg={formErrors.firstName}
          />
          <InputComponent
            id="middle-name"
            label="Middle Name"
            value={middleName}
            onChange={(e) => setMiddleName(e.currentTarget.value)}
            disabled={createContactResult.loading}
          />
          <InputComponent
            id="last-name"
            label="Last Name"
            value={lastName}
            onChange={handleLastNameChange}
            disabled={createContactResult.loading}
            errorMsg={formErrors.lastName}
          />
          <AutocompleteComponent
            id="contact-suffix"
            label="Suffix"
            options={Suffixes}
            getOptionLabel={(option: string) => option}
            disabled={createContactResult.loading}
            values={suffix}
            onChange={handleSuffixChange}
          />
          <InputComponent
            id="informal-name"
            label="Informal Name"
            value={informalName}
            onChange={(e) => setInformalName(e.currentTarget.value)}
            disabled={createContactResult.loading}
          />
        </Box>
        <Box sx={{ gridColumn: 'span 5', display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <AutocompleteComponent
            id="title-internal"
            label="Title internal"
            options={titlesInternal.data?.result || []}
            getOptionLabel={(option: ITitleInternal) => option?.name}
            disabled={createContactResult.loading}
            values={titleInternal}
            onChange={handleTitleInternalChange}
            errorMsg={formErrors.titleInternal}
          />
          <InputComponent
            id="title-external"
            label="Title external"
            value={titleExternal}
            onChange={(e) => setTitleExternal(e.currentTarget.value)}
            disabled={createContactResult.loading}
          />
          <AutocompleteComponent
            id="company"
            label="Choose company"
            options={(companies.data?.result as never[]) || []}
            getOptionLabel={(option: ICompany) => option?.name}
            disabled={createContactResult.loading}
            values={company}
            onChange={handleCompanyChange}
            errorMsg={formErrors.company}
          />
          <InputComponent
            id="notes"
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            disabled={createContactResult.loading}
            multiline={true}
            rows={4}
          />
        </Box>
        <Box sx={{ gridColumn: 'span 4', display: 'flex', flexFlow: 'column', gap: '35px' }}>
          <InputComponent
            id="phone-direct"
            label="Phone (direct)"
            value={phoneDirect}
            onChange={(e) => setPhoneDirect(e.currentTarget.value)}
            disabled={createContactResult.loading}
            startAdornment={
              <Call
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(!createContactResult.loading)
                }}
              />
            }
          />
          <InputComponent
            id="mobile-phone"
            label="Mobile phone"
            value={mobilePhone}
            onChange={(e) => setMobilePhone(e.currentTarget.value)}
            disabled={createContactResult.loading}
            startAdornment={
              <StayCurrentPortrait
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(!createContactResult.loading)
                }}
              />
            }
          />
          <InputComponent
            id="fax-direct"
            label="Fax (direct)"
            value={faxDirect}
            onChange={(e) => setFaxDirect(e.currentTarget.value)}
            disabled={createContactResult.loading}
            startAdornment={
              <Print
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(!createContactResult.loading)
                }}
              />
            }
          />
          <InputComponent
            id="email"
            label="Email (direct)"
            value={email}
            onChange={handleEmailChange}
            disabled={createContactResult.loading}
            errorMsg={formErrors.email}
            startAdornment={
              <Email
                sx={{
                  marginRight: '13px',
                  color: getRequiredFieldLabelColor(!createContactResult.loading, formErrors.email)
                }}
              />
            }
          />
          <InputComponent
            id="location"
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.currentTarget.value)}
            disabled={createContactResult.loading}
            startAdornment={
              <LocationOn
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(!createContactResult.loading)
                }}
              />
            }
          />
          <InputComponent
            id="linkedIn"
            label="LinkedIn"
            value={linkedIn}
            onChange={(e) => setLinkedIn(e.currentTarget.value)}
            disabled={createContactResult.loading}
            startAdornment={
              <LinkedInIcon
                sx={{
                  marginRight: '13px',
                  color: getLabelColor(!createContactResult.loading)
                }}
              />
            }
          />
        </Box>
      </Box>
      <Box sx={{ marginTop: '43px' }}>
        <Divider style={{ background: '#69AAF34A' }} />
        <Box sx={{ marginTop: '12px' }}>
          <Box
            sx={{
              textAlign: 'left',
              fontSize: '14px',
              fontWeight: 'bold',
              color: Colors.main
            }}
          >
            ADD INTEREST
          </Box>
          <Box
            sx={{
              marginTop: '20px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0 60px'
            }}
          >
            {allInterests.map((i, index) => (
              <CheckboxComponent
                key={index}
                id={i.id}
                label={i.name}
                value={contactInterestIds.includes(i.id)}
                onChange={() => handleCheckClicked(i?.id as string)}
                disabled={createContactResult.loading}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Box sx={{ color: Colors.red, marginTop: '20px', height: '18px' }}>
        {formErrors.interests}
      </Box>
      <Box className="centralize" sx={{ marginTop: '60px' }}>
        <CancelButton
          id="cancel-new-contact-button"
          disabled={createContactResult.loading}
          onClick={handleClose}
          text="Cancel"
        />
        <SaveButton
          id="create-new-contact-button"
          disabled={createContactResult.loading}
          onClick={submitForm}
          text="Create"
        />
      </Box>
    </DialogBox>
  );
};

export default NewContact;
